import { FormikProps } from 'formik';
import { makeStyles, mergeClasses } from '@fluentui/react-components';
import {
    AddCircle12Regular,
    ContactCardGroupRegular,
    PersonAddRegular,
} from '@fluentui/react-icons';
import { useAccess } from '../../../../auth/useAccess';
import { useAccountState } from '../../../../redux/reducers/accountReducer';
import { BrandCard, Column } from '../../../common';
import { PersonControl } from '../../../controls/PersonControl';
import { SelectUsersDialog } from '../../../dialogs/SelectUsersDialog';
import { Person, Case, UserRef } from '../../../../data/types';
import { LinkButton } from '../../../controls/LinkButton';

export const useStyles = makeStyles({
    root: {},
    user: {
        width: '13.5rem',
    },
});
export type CaseAssignedToProps = {
    className?: string;
    caseForm?: FormikProps<Case>;
    createMode?: boolean;
    editMode?: boolean;
};
export const CaseAssignedToCard = (props: CaseAssignedToProps) => {
    const { className, caseForm, createMode = false, editMode = false } = props;

    const caseObj = caseForm?.values as Case;

    //Hooks
    const { primaryAccount } = useAccountState();
    const access = useAccess(primaryAccount?.user);

    //Styles
    const styles = useStyles();
    const cardClasses = mergeClasses(styles.root, className);

    //Access
    const canRemoveAssignedUser = () => {
        if (!!createMode && access.account.canCreateCases()) {
            return true;
        }

        if (!!editMode) {
            return access.cases.canEditCaseInfo(caseObj?.caseId);
        }

        return false;
    };

    //Logic
    const onSubmit = (users: UserRef[]) => {
        const joinedUsers = [...(caseObj.assignedUsers || []), ...users];
        const distinctUsers = [...new Set(joinedUsers)];

        caseForm?.setFieldValue('assignedUsers', distinctUsers);
    };
    const onRemoveAssignedUser = (user: Person) => {
        if (!canRemoveAssignedUser()) return;

        const filteredUsers =
            caseObj?.assignedUsers?.filter((u) => u.userId != (user as UserRef).userId) || [];
        caseForm?.setFieldValue('assignedUsers', filteredUsers);

        if (caseForm?.values.caseLead == user) {
            caseForm?.setFieldValue('caseLead', undefined);
        }
    };

    return (
        <>
            <BrandCard
                className={cardClasses}
                title='Assigned To'
                headerIcon={<PersonAddRegular />}
                minHeight='fit-content'
                data-testid='case-assigned-to-card'
                action={
                    !!access.cases.canEditCaseAsAdmin(caseObj?.caseId) ? (
                        <SelectUsersDialog
                            title='Assign Case To'
                            icon={<ContactCardGroupRegular />}
                            assignedUsers={caseObj.assignedUsers}
                            onSubmit={onSubmit}
                        >
                            <LinkButton
                                icon={<AddCircle12Regular />}
                                text='Assign Case'
                                data-testid='case-assigned-to-add-btn'
                            />
                        </SelectUsersDialog>
                    ) : (
                        <></>
                    )
                }
            >
                <Column data-testid='case-assigned-to-content' gap='medium'>
                    {caseObj.assignedUsers?.map((userRef, index) => (
                        <PersonControl
                            key={userRef?.userId}
                            size='medium'
                            person={userRef}
                            onRemove={() => onRemoveAssignedUser(userRef)}
                            data-testid={`case-assigned-to-user-${index}`}
                        />
                    ))}
                </Column>
            </BrandCard>
        </>
    );
};
